import React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { Select, SelectContent, SelectGroup, SelectLabel, SelectValue } from '@/src/renderer/components/ui/select'

export interface PickerSelectOption {
  value: string
  label: string
  icon?: React.ReactNode
}

export interface PickerSelectGroup {
  /** Beschriftung der Gruppe; fehlt sie, stehen die Einträge ohne Überschrift da. */
  label?: string
  options: PickerSelectOption[]
}

interface PickerSelectProps {
  value: string
  onValueChange: (value: string) => void
  groups: PickerSelectGroup[]
  'aria-label': string
  /**
   * **Natives `<select>` statt Radix.** In den Popovern steht der Wiederkehr-Wähler in
   * einem Radix-Untermenü in einem floating-ui-Portal; ein drittes Portal kämpfte um
   * Fokus und Ebenen. In den **Aufgaben-Details** gibt es keine Hülle, dort darf es das
   * gestaltete Auswahlfeld sein.
   */
  native: boolean
  /** Die Hauptwahl („Wiederholen:") steht in Medium, die Felder im Bauplatz in Regular. */
  emphasized?: boolean
}

/**
 * **Ein Auswahlfeld in zwei Kleidern, mit EINEM Aussehen im Ruhezustand** (Figma
 * `5372:178399`): 36px hoch, Zeichen, Wort, Pfeil. Nur was aufklappt, unterscheidet
 * sich — siehe `native`.
 *
 * Nativ liegt das echte `<select>` unsichtbar ÜBER dem gezeichneten Feld: Es fängt
 * Klick, Tastatur und Vorleseprogramm; zu sehen ist das Feld darunter.
 */
export const PickerSelect: React.FC<PickerSelectProps> = ({
  value, onValueChange, groups, 'aria-label': ariaLabel, native, emphasized = false,
}) => {
  const current = groups.flatMap(group => group.options).find(option => option.value === value)
  const className = `picker-select${emphasized ? ' picker-select--emphasized' : ''}`

  if (native) {
    return (
      <span className={`${className} picker-select--native`}>
        <span className="picker-select__trigger" aria-hidden="true">
          {current?.icon && <span className="picker-select__icon">{current.icon}</span>}
          <span className="picker-select__label">{current?.label}</span>
          <ChevronDown size={16} className="picker-select__chevron" />
        </span>
        <select
          className="picker-select__native"
          aria-label={ariaLabel}
          value={value}
          onChange={e => onValueChange(e.target.value)}
        >
          {groups.map((group, index) => group.label
            ? (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </optgroup>
            )
            : group.options.map(option => <option key={`${index}:${option.value}`} value={option.value}>{option.label}</option>))}
        </select>
      </span>
    )
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      {/* Der Auslöser direkt aus Radix: `SelectTrigger` aus components/ui bringt volle Breite
          und `!important`-Regeln mit — hier ist es Inhaltsbreite. */}
      <SelectPrimitive.Trigger className={`${className} picker-select__trigger`} aria-label={ariaLabel}>
        {current?.icon && <span className="picker-select__icon">{current.icon}</span>}
        <span className="picker-select__label"><SelectValue /></span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown size={16} className="picker-select__chevron" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectContent className="picker-select__content">
        {groups.map((group, index) => (
          <SelectGroup key={group.label ?? index}>
            {group.label && <SelectLabel className="picker-select__group-label">{group.label}</SelectLabel>}
            {group.options.map(option => (
              // Vorn das Zeichen, hinten der Haken. `SelectValue` im Auslöser spiegelt nur
              // den `ItemText` — das Zeichen dort zeichnet der Auslöser selbst.
              <SelectPrimitive.Item key={option.value} value={option.value} className="picker-select__item">
                {option.icon && <span className="picker-select__item-icon">{option.icon}</span>}
                <span className="picker-select__item-label">
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </span>
                <span className="picker-select__item-check">
                  <SelectPrimitive.ItemIndicator><Check size={16} /></SelectPrimitive.ItemIndicator>
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )
}
